import { useCallback, useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { Calendar } from "@/components/ui/calendar";
import { useTheme } from "@/components/Theme";
import { calendarDateToMs, msToCalendarDate } from "./dateBoundValue";

interface DateRangeCalendarProps {
  from?: number;
  to?: number;
  onChange: (from: number, to: number) => void;
}

function toSelected(from?: number, to?: number): DateRange | undefined {
  const fromDate = msToCalendarDate(from);
  return fromDate != null
    ? { from: fromDate, to: msToCalendarDate(to) }
    : undefined;
}

export function DateRangeCalendar({
  from,
  to,
  onChange,
}: DateRangeCalendarProps) {
  const { isDarkMode } = useTheme();
  // the half-picked range between the two clicks has no filter to represent it, so it lives here
  const [selected, setSelected] = useState<DateRange | undefined>(() =>
    toSelected(from, to)
  );

  useEffect(() => {
    setSelected(toSelected(from, to));
  }, [from, to]);

  const handleSelect = useCallback(
    (range: DateRange | undefined) => {
      setSelected(range);

      if (range?.from != null && range.to != null) {
        onChange(
          calendarDateToMs(range.from, "start"),
          calendarDateToMs(range.to, "end")
        );
      }
    },
    [onChange]
  );

  // portalled out of the themed root, so the shadcn dark variants need the class back
  return (
    <div className={isDarkMode ? "dark" : "light"}>
      <Calendar
        initialFocus
        mode="range"
        defaultMonth={selected?.from}
        selected={selected}
        onSelect={handleSelect}
        numberOfMonths={1}
      />
    </div>
  );
}
